import { useCallback } from 'react';
import { create } from 'zustand';
import { useNotification } from '@libs/snackbar';
import { RecurrentShift } from '@libs/models/shifts';
import { parseError } from '@libs/api/errors';
import { Analytics } from '@libs/analytics';
import {
  assignShift,
  assignShifts,
  editRecurringShift,
  EditRecurringShiftRequestData,
  unassignShift,
} from '@libs/api/shifts';
import { shiftIsSeries } from '@libs/shifts';
import { getShift, getShiftRotation, getShiftSeries } from '@libs/api/shifts/getShift';

type State = {
  loading: boolean;
  seriesOpened: boolean;
  rotationOpened: boolean;
  seriesId: string;
  initiated: boolean;
  data: RecurrentShift | null;
  load: (id: string, recurrenceType: 'series' | 'rotation') => Promise<void>;
  /**
   * @deprecated
   */
  openShiftRecurrenceDrawer: (id: string) => Promise<void>;
  openShiftSeriesDrawer: (id: string) => Promise<void>;
  openShiftRotationDrawer: (id: string) => Promise<void>;
  closeShiftRecurrenceDrawer: () => void;
  saveRecurrence: (id: string, shift: EditRecurringShiftRequestData) => Promise<void>;
  assignShifts: (shiftIds: string[], userId: string) => Promise<void>;
  assignShift: (shiftId: string, userId: string) => Promise<void>;
  unassignShift: (shiftId: string, data: { reason: string; reasonComment: string }) => Promise<void>;
  assignSubmitting: boolean;
};

export const useShiftRecurrenceEditStore = create<State>()((set, get) => ({
  loading: false,
  seriesOpened: false,
  rotationOpened: false,
  initiated: false,
  seriesId: '',
  data: null,
  assignSubmitting: false,
  load: async (id, recurrenceType) => {
    set({ loading: true });
    try {
      const resp = recurrenceType === 'series' ? await getShiftSeries(id) : await getShiftRotation(id);
      set({
        loading: false,
        data: {
          ...resp,
          id: resp.id,
          startDate: resp.startDate.dateTime,
          endDate: resp.endDate.dateTime,
          externalId: resp.externalId,
          department: resp.department.label,
          subDepartment: resp.subDepartment?.label ?? null,
          speciality: resp.speciality?.id ?? null,
          specialty: resp.speciality ?? null,
          qualifications: resp.staffNurseQualificationFilters,
          assignees: resp.staff.map((s) => ({
            id: s.id,
            name: [s.firstName, s.lastName].filter(Boolean).join(' '),
            avatar: s.imageURL,
          })),
          isShared: resp.isShared,
          events: resp.events.map((event) => ({
            id: event.id,
            startDate: event.startDate.dateTime,
            endDate: event.endDate.dateTime,
            timeZone: event.startDate.timeZone,
            status: event.status,
            recurrence: recurrenceType,
            recurrenceId: resp.id,
            assignee: event.staff
              ? {
                  id: event.staff.id,
                  name: [event.staff.firstName, event.staff.lastName].filter(Boolean).join(' '),
                  avatar: event.staff.imageURL,
                }
              : null,
            qualifications: resp.staffNurseQualificationFilters,
          })),
          locationId: resp.locationId,
          tags: Array.from(new Set(resp.tags || [])),
          notes: resp.note ? [resp.note] : [],
          allowedMultipleAssignees: !!resp.canHasMultipleAssignee,
          canAssign: !!resp.canAssign,
          canUnassign: !!resp.canUnassign,
          isArchived: !!resp.isArchive,
          isUrgent: !!resp.isUrgent,
        },
      });
    } finally {
      set({ loading: false, initiated: true });
    }
  },
  openShiftRecurrenceDrawer: async (id) => {
    if (shiftIsSeries(id)) {
      set({ seriesOpened: true });
    } else {
      set({ rotationOpened: true });
    }
    set({ seriesId: id });
    // await get().load(id);
  },
  openShiftSeriesDrawer: async (id) => {
    set({ seriesOpened: true, seriesId: id });
    await get().load(id, 'series');
  },
  openShiftRotationDrawer: async (id) => {
    set({ rotationOpened: true, seriesId: id });
    await get().load(id, 'rotation');
  },
  closeShiftRecurrenceDrawer: () => {
    set({ seriesOpened: false, rotationOpened: false, data: null, seriesId: '' });
  },
  saveRecurrence: async (id, series) => {
    await editRecurringShift(id, series);
  },
  assignShift: async (shiftId: string, userId: string) => {
    set({ assignSubmitting: true });
    try {
      await assignShift(shiftId, userId);
      Analytics.track('Shift: Assigned', {
        shift_ids: shiftId,
      });
    } finally {
      set({ assignSubmitting: false });
    }
  },
  assignShifts: async (shiftsId: string[], userId: string) => {
    set({ assignSubmitting: true });
    try {
      await assignShifts(shiftsId, userId);
      Analytics.track('Shifts: Assigned', {
        shift_ids: shiftsId.join(', '),
      });
    } finally {
      set({ assignSubmitting: false });
    }
  },
  unassignShift: async (shiftId: string, data) => {
    set({ assignSubmitting: true });
    try {
      await unassignShift(shiftId, data.reason === 'other' ? data.reasonComment : data.reason);
      Analytics.track('Shift: Unassigned', {
        shift_id: shiftId,
        unassignment_reason: data.reason,
      });
    } finally {
      set({ assignSubmitting: false });
    }
  },
}));

/**
 * @deprecated
 */
export function useRecurrenceDrawerState() {
  const { showErrorIntl } = useNotification();
  const open = useShiftRecurrenceEditStore((state) => state.openShiftRecurrenceDrawer);
  const close = useShiftRecurrenceEditStore((state) => state.closeShiftRecurrenceDrawer);

  return [
    useCallback(
      async (id: string) => {
        try {
          await open(id);
        } catch (error) {
          showErrorIntl(parseError(error).message);
          throw error;
        }
      },
      [open, showErrorIntl],
    ),
    close,
  ] as const;
}
